import React, { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { appTypographySx } from '../theme';
import { SERIF, SANS, MONO, todayTokens } from '../theme/todayTokens';
import { recordStyles } from './property/PropertyRecord';

const ArchivedProperties = () => {
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  const s = recordStyles(isDarkMode);
  const { currentUser } = useAuth();
  const [archivedProperties, setArchivedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchivedProperties();
  }, [currentUser]);

  const fetchArchivedProperties = async () => {
    try {
      setLoading(true);
      const propertiesRef = collection(db, 'properties');
      const q = query(propertiesRef, where('archived', '==', true));
      const querySnapshot = await getDocs(q);

      const properties = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setArchivedProperties(properties);
    } catch (err) {
      console.error('Error fetching archived properties:', err);
      setError('Failed to load archived properties');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (propertyId) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        archived: false,
        archivedDate: null
      });

      // Refresh the list
      await fetchArchivedProperties();
    } catch (err) {
      console.error('Error unarchiving property:', err);
      setError('Failed to unarchive property');
    }
  };

  const goBack = () => {
    navigate('/properties');
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, ...appTypographySx(isDarkMode) }}>
        <Box component="span" sx={s.eyebrow}>Loading…</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, ...appTypographySx(isDarkMode) }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box role="main" aria-label="Archived Properties" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, ...appTypographySx(isDarkMode) }}>
      {/* ── Header ── */}
      <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
        <Box>
          <Box component="button" type="button" onClick={goBack} sx={{ ...s.underlineLink, minHeight: 36, fontSize: 13 }}>
            Back to properties
          </Box>
        </Box>
        <Box sx={{ ...s.eyebrow, fontSize: 12 }}>Archive · Properties</Box>
        <Box component="h1" sx={{ m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 0.95, letterSpacing: '-0.02em', color: t.ink }}>
          Archived Properties
        </Box>
      </Box>

      {archivedProperties.length === 0 ? (
        <Alert severity="info">No archived properties found</Alert>
      ) : (
        <Box sx={{ ...s.card, overflow: 'hidden' }}>
          {archivedProperties.map((property) => (
            <Box
              key={property.id}
              role="article"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: { xs: 2, sm: 2.5 },
                py: 2,
                borderBottom: `1px solid ${t.lineSoft}`,
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontFamily: SANS, fontSize: 15, fontWeight: 600, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {property.address}
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', color: t.muted, mt: 0.5 }}>
                  Archived on: {formatDate(property.archivedDate)}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => handleUnarchive(property.id)}
                aria-label="Unarchive property"
                sx={{ ...s.underlineLink, minHeight: 36, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}
              >
                Unarchive
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ArchivedProperties;
